import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Play, Square, Music, VolumeX, SkipForward, Moon, Infinity as InfIcon, Clock, Expand, Shrink } from "lucide-react";
import { suantraiPoems } from "@/lib/api";
import { useLang } from "@/lib/lang";
import { SUANTRAI_TRACKS } from "@/lib/tracks";

const SLEEP_TIMER_OPTIONS = [
  { minutes: 0,  label_fr: "Pas de minuteur", label_en: "No timer" },
  { minutes: 15, label_fr: "15 min",          label_en: "15 min" },
  { minutes: 30, label_fr: "30 min",          label_en: "30 min" },
  { minutes: 60, label_fr: "60 min",          label_en: "60 min" },
];

export default function Suantrai() {
  const { lang, setLang } = useLang();
  const [poems, setPoems] = useState([]);
  const [speakingId, setSpeakingId] = useState(null);
  const [musicOn, setMusicOn] = useState(false);
  const [trackIdx, setTrackIdx] = useState(() => {
    const idx = SUANTRAI_TRACKS.findIndex((t) => t.id === "gregorian");
    return idx >= 0 ? idx : 0;
  });
  const [sleepMinutes, setSleepMinutes] = useState(15);
  const [sleepRemaining, setSleepRemaining] = useState(0);
  const [immersive, setImmersive] = useState(false);
  const [immersivePoemIdx, setImmersivePoemIdx] = useState(0);
  const audioRef = useRef(null);
  const sleepIntervalRef = useRef(null);

  // Auto-start music softly on first mount — only if a sleep timer gets picked.
  // We also autoplay bg music when the user enters immersive mode.
  const ensureMusicOn = useCallback(() => {
    const el = audioRef.current;
    if (!el || musicOn) return;
    el.volume = 0.22;
    el.loop = false;
    el.load();
    el.play().then(() => setMusicOn(true)).catch(() => { /* user interaction required */ });
  }, [musicOn]);

  useEffect(() => {
    let cancelled = false;
    suantraiPoems(lang).then((r) => { if (!cancelled) setPoems(r.poems || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [lang]);

  useEffect(() => {
    return () => {
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
      if (audioRef.current) { try { audioRef.current.pause(); } catch { /* ignore */ } }
      if (sleepIntervalRef.current) { clearInterval(sleepIntervalRef.current); }
    };
  }, []);

  // Sleep timer: counts down and stops music + TTS when it reaches 0
  useEffect(() => {
    if (sleepIntervalRef.current) {
      clearInterval(sleepIntervalRef.current);
      sleepIntervalRef.current = null;
    }
    if (!sleepMinutes) {
      setSleepRemaining(0);
      return;
    }
    setSleepRemaining(sleepMinutes * 60);
    sleepIntervalRef.current = setInterval(() => {
      setSleepRemaining((s) => {
        if (s <= 1) {
          clearInterval(sleepIntervalRef.current);
          sleepIntervalRef.current = null;
          try { audioRef.current?.pause(); } catch { /* ignore */ }
          try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
          setMusicOn(false);
          setSpeakingId(null);
          toast.success(lang === "fr" ? "Minuteur écoulé. Bon sommeil 🌙" : "Timer ended. Sleep well 🌙");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (sleepIntervalRef.current) clearInterval(sleepIntervalRef.current);
    };
  }, [sleepMinutes, lang]);

  const toggleMusic = () => {
    const el = audioRef.current;
    if (!el) return;
    if (musicOn) { el.pause(); setMusicOn(false); return; }
    el.volume = 0.22;
    el.loop = false;
    const p = el.play();
    if (p && p.catch) {
      p.then(() => setMusicOn(true)).catch(() => toast.error(lang === "fr" ? "Audio indisponible." : "Audio unavailable."));
    } else setMusicOn(true);
  };
  const nextTrack = () => setTrackIdx((i) => (i + 1) % SUANTRAI_TRACKS.length);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const handler = () => setTrackIdx((i) => (i + 1) % SUANTRAI_TRACKS.length);
    el.addEventListener("ended", handler);
    return () => el.removeEventListener("ended", handler);
  }, []);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !musicOn) return;
    el.load();
    el.play().catch(() => {});
  }, [trackIdx, musicOn]);

  const stopSpeaking = () => {
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    setSpeakingId(null);
  };

  const speak = (poem) => {
    if (!("speechSynthesis" in window)) {
      toast.error(lang === "fr" ? "Votre navigateur ne supporte pas la lecture audio." : "Your browser does not support speech.");
      return;
    }
    stopSpeaking();
    const utter = new SpeechSynthesisUtterance(poem.text);
    utter.lang = lang === "en" ? "en-GB" : "fr-FR";
    utter.rate = 0.74;   // very slow for sleep
    utter.pitch = 0.52;  // deep masculine lullaby voice
    utter.volume = 1.0;
    try {
      const voices = window.speechSynthesis.getVoices();
      const prefix = utter.lang.slice(0, 2);
      const preferred =
        voices.find((v) => v.lang?.startsWith(prefix) && /thomas|daniel|arthur|nicolas|paul|henri|antoine|luc|male|homme/i.test(v.name))
        || voices.find((v) => v.lang?.startsWith(prefix) && !/female|femme|amelie|marie|julie|virginie/i.test(v.name))
        || voices.find((v) => v.lang?.startsWith(prefix));
      if (preferred) utter.voice = preferred;
    } catch { /* ignore */ }
    utter.onend = () => setSpeakingId((cur) => (cur === poem.id ? null : cur));
    utter.onerror = () => setSpeakingId((cur) => (cur === poem.id ? null : cur));
    setSpeakingId(poem.id);
    window.speechSynthesis.speak(utter);
  };

  const subtitle = useMemo(() => lang === "fr"
    ? "Suantraí (gaélique irlandais : berceuse) — vingt chants pour s'endormir, en voix de barde grave et musique celtique en boucle. Totalement gratuit, illimité, sans compte, sans chatbot. Juste la voix, les mots, le souffle qui ralentit."
    : "Suantraí (Irish Gaelic: lullaby) — twenty songs to fall asleep to, in deep bard voice with looping Celtic music. Fully free, unlimited, no account, no chatbot. Just voice, words, breath that slows.",
    [lang]);

  return (
    <div data-testid="suantrai-page" className="suan-root min-h-screen relative">
      <audio ref={audioRef} src={SUANTRAI_TRACKS[trackIdx].url} preload="none" crossOrigin="anonymous" />
      <div className="suan-bg" aria-hidden="true" />

      <header className="relative z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#8fb3d4" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
                 style={{ background: "rgba(30,50,70,0.5)", border: "1px solid rgba(143,179,212,0.3)" }}>
              <button onClick={() => setLang("fr")} type="button" className="px-2.5 py-1.5" data-testid="suantrai-lang-fr"
                      style={{ background: lang === "fr" ? "#8fb3d4" : "transparent", color: lang === "fr" ? "#06111a" : "#c3d8e8", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
              <button onClick={() => setLang("en")} type="button" className="px-2.5 py-1.5" data-testid="suantrai-lang-en"
                      style={{ background: lang === "en" ? "#8fb3d4" : "transparent", color: lang === "en" ? "#06111a" : "#c3d8e8", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
            </div>
            <button type="button" onClick={toggleMusic} data-testid="suantrai-music-toggle"
                    className="inline-flex items-center gap-2 text-[11px] font-mono px-3 py-2 rounded-full"
                    style={{ background: musicOn ? "rgba(143,179,212,0.22)" : "rgba(10,20,30,0.55)",
                             border: "1px solid rgba(143,179,212,0.35)",
                             color: musicOn ? "#c3d8e8" : "rgba(195,216,232,0.7)" }}>
              {musicOn ? <VolumeX size={13} /> : <Music size={13} />}
              {SUANTRAI_TRACKS[trackIdx].label} · {musicOn ? "on" : "off"}
            </button>
            {musicOn && (
              <button type="button" onClick={nextTrack} data-testid="suantrai-music-next"
                      className="hidden md:inline-flex items-center gap-1 text-[11px] font-mono px-3 py-2 rounded-full"
                      style={{ background: "rgba(10,20,30,0.55)", border: "1px solid rgba(143,179,212,0.3)", color: "#c3d8e8" }}>
                <SkipForward size={12} /> {lang === "fr" ? "Piste suivante" : "Next track"}
              </button>
            )}
            {/* Sleep timer selector */}
            <div className="inline-flex items-center rounded-full overflow-hidden text-[11px] font-mono"
                 data-testid="suantrai-sleep-timer"
                 style={{ background: "rgba(10,20,30,0.55)", border: "1px solid rgba(143,179,212,0.4)" }}>
              <span className="pl-3 pr-1 py-2 flex items-center gap-1" style={{ color: "#8fb3d4" }}>
                <Clock size={12} /> {lang === "fr" ? "Dormir dans" : "Sleep in"}
              </span>
              {SLEEP_TIMER_OPTIONS.map((opt) => (
                <button key={opt.minutes}
                        type="button"
                        onClick={() => { setSleepMinutes(opt.minutes); if (opt.minutes) ensureMusicOn(); }}
                        data-testid={`suantrai-timer-${opt.minutes}`}
                        className="px-2.5 py-2 transition-colors"
                        style={{
                          background: sleepMinutes === opt.minutes ? "#8fb3d4" : "transparent",
                          color: sleepMinutes === opt.minutes ? "#06111a" : "#c3d8e8",
                          fontWeight: sleepMinutes === opt.minutes ? 600 : 400,
                        }}>
                  {opt.minutes === 0 ? (lang === "fr" ? "Off" : "Off") : `${opt.minutes}m`}
                </button>
              ))}
            </div>
            {/* Immersive full-screen toggle */}
            <button type="button"
                    onClick={() => { setImmersive(true); ensureMusicOn(); }}
                    data-testid="suantrai-immersive-enter"
                    className="inline-flex items-center gap-1.5 text-[11px] font-mono px-3 py-2 rounded-full"
                    style={{ background: "rgba(143,179,212,0.18)", border: "1px solid rgba(143,179,212,0.5)", color: "#c3d8e8" }}>
              <Expand size={12} /> {lang === "fr" ? "Mode immersif" : "Immersive"}
            </button>
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-4 pb-8">
        <div className="overline mb-4" style={{ color: "#8fb3d4" }}>
          <span className="kicker-line" />
          {lang === "fr" ? "Suantraí · berceuses celtiques · gratuit · illimité · sans compte" : "Suantraí · celtic lullabies · free · unlimited · no account"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f0f4f8" }}>
          {lang === "fr" ? (
            <><span className="italic" style={{ color: "#8fb3d4" }}>Suantraí</span>, le souffle qui ralentit.</>
          ) : (
            <><span className="italic" style={{ color: "#8fb3d4" }}>Suantraí</span>, the breath that slows.</>
          )}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "#c3d8e8" }}>
          {subtitle}
        </p>
        <div data-testid="suantrai-badge" className="mt-6 inline-flex items-center gap-2 text-[11px] font-mono px-4 py-2 rounded-full"
             style={{ background: "rgba(14,24,36,0.6)", border: "1px solid rgba(143,179,212,0.35)", color: "#8fb3d4" }}>
          <InfIcon size={13} /> {lang === "fr" ? "Lectures illimitées · aucune limite" : "Unlimited playback · no cap"}
        </div>
        {sleepMinutes > 0 && sleepRemaining > 0 && (
          <div data-testid="suantrai-sleep-remaining" className="mt-3 inline-flex items-center gap-2 ml-0 md:ml-3 text-[11px] font-mono px-4 py-2 rounded-full"
               style={{ background: "rgba(14,24,36,0.6)", border: "1px solid rgba(143,179,212,0.5)", color: "#c3d8e8" }}>
            <Clock size={13} /> {lang === "fr" ? "Reste" : "Remaining"} :{" "}
            {Math.floor(sleepRemaining / 60)}:{String(sleepRemaining % 60).padStart(2, "0")}
          </div>
        )}
      </section>

      <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-16">
        {poems.length === 0 ? (
          <div className="text-sm font-mono py-16 text-center" style={{ color: "#8fa5bc" }}>
            {lang === "fr" ? "Les berceuses s'accordent, un instant…" : "The lullabies are tuning, one moment…"}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {poems.map((poem) => {
              const speaking = speakingId === poem.id;
              return (
                <article key={poem.id} data-testid={`suantrai-card-${poem.id}`} className={`suan-card ${speaking ? "speaking" : ""}`}>
                  <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.24em]" style={{ color: "#8fb3d4" }}>
                    <Moon size={11} />
                    <span>{lang === "fr" ? "Suantraí" : "Suantraí"}</span>
                    {poem.mood && <>
                      <span style={{ color: "rgba(195,216,232,0.5)" }}>·</span>
                      <span style={{ color: "#c3d8e8" }}>{poem.mood}</span>
                    </>}
                  </div>
                  <h3 className="mt-3 font-serif text-2xl leading-tight" style={{ color: "#f0f4f8" }}>{poem.title}</h3>
                  {poem.subtitle && (
                    <p className="mt-1 text-sm italic" style={{ color: "#c3d8e8" }}>{poem.subtitle}</p>
                  )}
                  <div className="mt-4 suan-poem">{poem.text}</div>
                  <div className="mt-5 flex items-center gap-2">
                    {!speaking ? (
                      <button onClick={() => speak(poem)} data-testid={`suantrai-play-${poem.id}`} type="button"
                              className="inline-flex items-center gap-2 text-sm font-mono px-4 py-2 rounded-full"
                              style={{ background: "#8fb3d4", color: "#06111a", fontWeight: 600 }}>
                        <Play size={13} /> {lang === "fr" ? "Écouter" : "Listen"}
                      </button>
                    ) : (
                      <button onClick={stopSpeaking} data-testid={`suantrai-stop-${poem.id}`} type="button"
                              className="inline-flex items-center gap-2 text-sm font-mono px-4 py-2 rounded-full"
                              style={{ background: "rgba(143,179,212,0.18)", color: "#8fb3d4", border: "1px solid rgba(143,179,212,0.5)" }}>
                        <Square size={13} /> {lang === "fr" ? "Arrêter" : "Stop"}
                      </button>
                    )}
                    {speaking && (
                      <span className="text-[11px] font-mono animate-pulse" style={{ color: "#8fb3d4" }}>
                        {lang === "fr" ? "Berceuse en cours…" : "Lullaby playing…"}
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* ============ IMMERSIVE FULL-SCREEN MODE ============ */}
      {immersive && (() => {
        const total = sleepMinutes * 60 || 1;
        const starOpacity = sleepMinutes
          ? Math.max(0.08, sleepRemaining / total)
          : 1;
        const curPoem = poems[immersivePoemIdx] || null;
        return (
          <div data-testid="suantrai-immersive-overlay"
               style={{ position: "fixed", inset: 0, zIndex: 9998,
                        background: "radial-gradient(ellipse at center, #060a16 0%, #02040a 70%, #000 100%)",
                        overflow: "hidden", display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center" }}>
            <div className="imm-stars imm-stars-1" style={{ opacity: starOpacity }} />
            <div className="imm-stars imm-stars-2" style={{ opacity: starOpacity * 0.75 }} />
            <div className="imm-stars imm-stars-3" style={{ opacity: starOpacity * 0.5 }} />
            <div style={{ position: "absolute", top: 20, left: 0, right: 0,
                          display: "flex", justifyContent: "space-between",
                          padding: "0 24px", zIndex: 2 }}>
              <button type="button" data-testid="suantrai-immersive-exit"
                      onClick={() => setImmersive(false)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6,
                               padding: "8px 14px", borderRadius: 999,
                               background: "rgba(10,20,30,0.6)", color: "#8fb3d4",
                               border: "1px solid rgba(143,179,212,0.3)", fontSize: 11,
                               fontFamily: "'IBM Plex Mono',monospace",
                               letterSpacing: "0.18em", textTransform: "uppercase" }}>
                <Shrink size={13} /> {lang === "fr" ? "Quitter" : "Exit"}
              </button>
              {sleepMinutes > 0 && sleepRemaining > 0 && (
                <span data-testid="suantrai-immersive-countdown"
                      style={{ padding: "8px 14px", borderRadius: 999,
                               background: "rgba(10,20,30,0.6)", color: "#c3d8e8",
                               border: "1px solid rgba(143,179,212,0.3)", fontSize: 11,
                               fontFamily: "'IBM Plex Mono',monospace",
                               letterSpacing: "0.18em" }}>
                  <Clock size={12} style={{ display: "inline", marginRight: 6, verticalAlign: "-2px" }} />
                  {Math.floor(sleepRemaining / 60)}:{String(sleepRemaining % 60).padStart(2, "0")}
                </span>
              )}
            </div>
            {curPoem && (
              <div style={{ position: "relative", zIndex: 2, maxWidth: 720,
                            padding: "0 32px", textAlign: "center", color: "#f0f4f8" }}>
                <div style={{ fontSize: 11, fontFamily: "'IBM Plex Mono',monospace",
                              letterSpacing: "0.26em", textTransform: "uppercase",
                              color: "#8fb3d4", marginBottom: 18 }}>
                  Suantraí · {curPoem.mood || "doux"}
                </div>
                <h2 style={{ fontFamily: "'Cormorant Garamond','Playfair Display',serif",
                             fontSize: "clamp(32px, 4vw, 48px)", fontStyle: "italic",
                             color: "#f0f4f8", marginBottom: 20, lineHeight: 1.15 }}>
                  {curPoem.title}
                </h2>
                <div style={{ whiteSpace: "pre-wrap",
                              fontFamily: "'Cormorant Garamond','Georgia',serif",
                              fontSize: "clamp(18px, 2vw, 22px)", lineHeight: 1.9,
                              color: "#dbe6ef", textShadow: "0 0 24px rgba(143,179,212,0.18)" }}>
                  {curPoem.text}
                </div>
                <div style={{ marginTop: 32, display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <button type="button" data-testid="suantrai-immersive-prev"
                          onClick={() => setImmersivePoemIdx((i) => (i - 1 + poems.length) % poems.length)}
                          style={{ padding: "10px 20px", borderRadius: 999,
                                   background: "transparent", color: "#8fb3d4",
                                   border: "1px solid rgba(143,179,212,0.4)", fontSize: 12,
                                   fontFamily: "'IBM Plex Mono',monospace",
                                   letterSpacing: "0.2em", textTransform: "uppercase" }}>
                    ← {lang === "fr" ? "Précédent" : "Prev"}
                  </button>
                  <button type="button" data-testid="suantrai-immersive-speak"
                          onClick={() => speak(curPoem)}
                          style={{ padding: "10px 20px", borderRadius: 999,
                                   background: speakingId === curPoem.id ? "rgba(143,179,212,0.3)" : "#8fb3d4",
                                   color: speakingId === curPoem.id ? "#8fb3d4" : "#06111a",
                                   border: "1px solid rgba(143,179,212,0.5)", fontSize: 12,
                                   fontFamily: "'IBM Plex Mono',monospace",
                                   letterSpacing: "0.2em", textTransform: "uppercase",
                                   fontWeight: 600 }}>
                    {speakingId === curPoem.id
                      ? (lang === "fr" ? "■ Pause" : "■ Pause")
                      : (lang === "fr" ? "▶ Écouter" : "▶ Listen")}
                  </button>
                  <button type="button" data-testid="suantrai-immersive-next"
                          onClick={() => setImmersivePoemIdx((i) => (i + 1) % poems.length)}
                          style={{ padding: "10px 20px", borderRadius: 999,
                                   background: "transparent", color: "#8fb3d4",
                                   border: "1px solid rgba(143,179,212,0.4)", fontSize: 12,
                                   fontFamily: "'IBM Plex Mono',monospace",
                                   letterSpacing: "0.2em", textTransform: "uppercase" }}>
                    {lang === "fr" ? "Suivant" : "Next"} →
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <style>{`
        .suan-root { background: radial-gradient(ellipse at top, #0e1a28 0%, #060e18 55%, #02070e 100%); color: #f0f4f8; overflow-x: hidden; }
        .suan-bg { position: fixed; inset: 0; z-index: 0; pointer-events: none;
          background:
            radial-gradient(circle at 18% 20%, rgba(143,179,212,0.14) 0%, transparent 42%),
            radial-gradient(circle at 82% 76%, rgba(90,130,170,0.12) 0%, transparent 42%),
            linear-gradient(180deg, #0e1a28 0%, #04070c 100%); }
        .suan-card {
          background: linear-gradient(180deg, rgba(14,26,38,0.92) 0%, rgba(6,14,22,0.95) 100%);
          border: 1px solid rgba(143,179,212,0.22);
          border-radius: 22px;
          padding: 24px;
          position: relative;
          overflow: hidden;
          transition: transform 260ms ease, border-color 260ms ease, box-shadow 260ms ease;
        }
        .suan-card:hover { transform: translateY(-4px); border-color: rgba(143,179,212,0.5); box-shadow: 0 24px 48px -20px rgba(143,179,212,0.3); }
        .suan-card.speaking { border-color: rgba(143,179,212,0.85); box-shadow: 0 0 0 1px rgba(143,179,212,0.4), 0 24px 52px -18px rgba(143,179,212,0.45); }
        .suan-poem { white-space: pre-wrap; font-family: "Cormorant Garamond", "Georgia", serif; font-size: 17px; line-height: 1.75; color: #e4ecf3; }

        /* ===== Immersive fading starfield ===== */
        .imm-stars {
          position: absolute;
          inset: 0;
          background-repeat: repeat;
          pointer-events: none;
          transition: opacity 1500ms linear;
          animation: imm-drift 160s linear infinite;
        }
        .imm-stars-1 {
          background-image: radial-gradient(1.4px 1.4px at 25px 45px, #ffffff 50%, transparent 50%),
                             radial-gradient(1px 1px at 120px 90px, #e8f0ff 50%, transparent 50%),
                             radial-gradient(1.6px 1.6px at 200px 170px, #ffffff 50%, transparent 50%),
                             radial-gradient(1px 1px at 60px 220px, #c3d8e8 50%, transparent 50%),
                             radial-gradient(1.2px 1.2px at 180px 280px, #ffffff 50%, transparent 50%);
          background-size: 260px 320px;
          animation-duration: 220s;
        }
        .imm-stars-2 {
          background-image: radial-gradient(1px 1px at 40px 60px, #d7e3f5 50%, transparent 50%),
                             radial-gradient(0.8px 0.8px at 150px 30px, #a8c0e0 50%, transparent 50%),
                             radial-gradient(1.4px 1.4px at 90px 200px, #ffffff 50%, transparent 50%),
                             radial-gradient(1px 1px at 220px 140px, #e8f0ff 50%, transparent 50%);
          background-size: 320px 240px;
          animation-duration: 180s;
          animation-direction: reverse;
        }
        .imm-stars-3 {
          background-image: radial-gradient(2px 2px at 100px 80px, #ffffff 50%, transparent 50%),
                             radial-gradient(0.8px 0.8px at 50px 180px, #8fb3d4 50%, transparent 50%),
                             radial-gradient(1.6px 1.6px at 240px 60px, #ffffff 50%, transparent 50%);
          background-size: 400px 300px;
          animation-duration: 300s;
        }
        @keyframes imm-drift {
          from { background-position: 0 0; }
          to   { background-position: 1000px 600px; }
        }
      `}</style>
    </div>
  );
}
